import React, { useState } from 'react';
import ToggleNav from '../ToggleNav';
import './OrderSummary.css';
import { useDataContext } from '../../context/data.js';
import translations from '../../constants/translations.json';
import { venueSellsOnline } from '../../utils/orderTypes';

const OrderSummary = ({
  items,
  grandTotal,
  onViewCart,
  orderType,
  orderTypeOptions,
  onOrderTypeChange,
  restaurant,
  // The guest is pre-ordering for a performance. The order-type toggle is not a
  // choice they still have — tapping it switches flow and DISCARDS the chosen
  // performance with no warning — so it is replaced by the mode's name, exactly
  // as the checkout page already does.
  isTheaterPreorder = false,
  currentStep,
  onNextStep,
  onPrevStep,
  onGoToSelectPaymentMethod,
  fromHeyCharity,
  // A delivery with no quote yet: its fee, and so the total, is not known.
  woltFeePending = false,
}) => {
  let pathname = window.location.pathname;
  const restaurantId = pathname.split('/')?.[2];
  const { language } = useDataContext();

  const [isExpanded, setIsExpanded] = useState(true);
  const formattedGrandTotal = typeof grandTotal === 'number' ? grandTotal.toFixed(2) : '0.00';
  const currencyCode = restaurant?.currency?.code || 'DKK';
  // The checkout steps show what the guest will pay, so they say the total is
  // pending rather than show one without the delivery fee (the app does too).
  const checkoutTotal = woltFeePending
    ? translations[language]?.wolt_delivery_fee_pending || 'Calculated after address'
    : `${currencyCode} ${formattedGrandTotal}`;

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (currentStep === 'choose-order') return null;

  return (
    <div
      className="order-summary-container fixed bottom-[0] left-0 right-0 w-full bg-white z-[90]"
      style={{ boxShadow: 'rgba(0, 0, 0, 0.05) 0px -4px 10px 0px' }}
    >
      {/* Mobile Toggle Button - Only visible on smallest screens */}
      {currentStep === 'menu' && restaurantId !== '682d79a98f4810001ac860e1' && (
        <div className="sm:hidden w-full relative">
          <button
            onClick={toggleExpand}
            className="absolute right-0 -top-7 h-7 w-7 bg-white rounded-tl-lg flex items-center justify-center"
            style={{ boxShadow: 'rgba(0, 0, 0, 0.05) 0px -4px 10px 0px' }}
            aria-label={isExpanded ? 'Collapse Order Summary' : 'Expand Order Summary'}
          >
            <i className={`fa fa-chevron-${isExpanded ? 'down' : 'up'}`}></i>
          </button>
        </div>
      )}

      <div
        className={`max-w-[80rem] mx-auto px-4 sm:px-4 py-3 pt-2 sm:py-[14px] sm:pt-[14px] ${
          !isExpanded ? 'max-h-[60px] sm:max-h-none overflow-hidden sm:overflow-visible' : ''
        } transition-all duration-300`}
      >
        {currentStep === 'menu' ? (
          <>
            {/* Expanded mobile view */}
            {isExpanded && !fromHeyCharity && restaurantId !== '682d79a98f4810001ac860e1' && (
              <div className="sm:hidden mb-2">
                <div className="w-full">
                  {isTheaterPreorder ? (
                    <div className="flex items-center rounded-full p-1 bg-gray-100 h-[40px] sm:h-[50px]">
                      <p className="py-[6px] sm:py-[11px] px-2 w-full text-sm text-center rounded-full font-semibold  flex-1 bg-primary text-white cursor-auto">
                        {translations[language]?.order_type_theater || 'Forudbestilling'}
                      </p>
                    </div>
                  ) : (
                    <ToggleNav
                      options={orderTypeOptions}
                      activeId={orderType}
                      onChange={onOrderTypeChange}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Always visible content on mobile */}
            <div className="flex items-center justify-between w-full sm:hidden">
              <div className="pointer-events-none w-[91px] px-3 py-[7px]" />
              <p className="text-xs font-bold text-gray-600 space-x-1">
                <span>
                  {items.length} {translations[language]?.item_length_title}
                </span>
                <span>
                  {currencyCode} {formattedGrandTotal}
                </span>
              </p>
              <button
                onClick={onViewCart}
                className={`flex items-center justify-center text-xs font-bold bg-primary text-white px-4 py-3 rounded-full lg:hover:bg-secondary transition-colors ${
                  (!restaurant.isMenuBuyable ||
                    restaurant.orderingPaused ||
                    !venueSellsOnline(restaurant)) &&
                  'hover:bg-primary !cursor-not-allowed'
                }`}
              >
                <span>{translations[language]?.order_summary_view_basket}</span>
                <i className="fa fa-chevron-right ml-1" />
              </button>
            </div>

            {/* Original desktop layout (unchanged) */}
            <div className="hidden sm:flex sm:flex-row items-center justify-between gap-0">
              <div
                className={`w-full ${orderTypeOptions?.length > 2 ? 'sm:w-72 lg:w-80' : 'sm:w-56 lg:w-64'}`}
              >
                {fromHeyCharity ? (
                  <ToggleNav
                    options={[{ id: 'billetter', label: 'Billetter mm.' }]}
                    activeId={'billetter'}
                    onChange={() => null}
                    size="default"
                    activeClassName="cursor-auto bg-primary text-white"
                  />
                ) : isTheaterPreorder ? (
                  <div className="flex items-center rounded-full p-1 bg-gray-100 h-[40px] sm:h-[50px]">
                    <p className="py-[6px] sm:py-[11px] px-2 w-full text-sm text-center rounded-full font-semibold  flex-1 bg-primary text-white cursor-auto">
                      {translations[language]?.order_type_theater || 'Forudbestilling'}
                    </p>
                  </div>
                ) : (
                  <>
                    {restaurantId !== '682d79a98f4810001ac860e1' && (
                      <ToggleNav
                        options={orderTypeOptions}
                        activeId={orderType}
                        onChange={onOrderTypeChange}
                        size="default"
                      />
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-col-reverse sm:flex-row items-center space-y-2 sm:space-y-0 space-x-0 sm:space-x-2 lg:sm:space-x-6 w-full sm:w-auto">
                <p className="text-sm font-bold text-gray-600 w-full sm:w-auto text-center sm:text-left !mt-2 sm:!mt-0">
                  <span className="text-md">
                    {items.length} {translations[language]?.item_length_title}
                  </span>
                  <span className="text-md ml-1 lg:ml-3">
                    {translations[language]?.total} {currencyCode} {formattedGrandTotal}
                  </span>
                </p>

                <button
                  onClick={onViewCart}
                  className={`flex items-center justify-center text-sm font-bold bg-primary text-white px-3 lg:!px-8 py-[15px] rounded-full lg:hover:bg-secondary transition-colors w-full sm:w-auto ${
                    (!restaurant.isMenuBuyable ||
                    restaurant.orderingPaused ||
                    !venueSellsOnline(restaurant)) &&
                    'hover:bg-primary !cursor-not-allowed'
                  }`}
                >
                  <span>{translations[language]?.order_summary_view_basket_and_order}</span>
                  <i className="fa fa-chevron-right ml-2" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="max-w-[80rem] mx-auto flex flex-row items-center justify-between">
              <div className="hidden sm:block">
                <button
                  className="bg-[#fff] hover:bg-primary/20 border !border-primary text-sm text-primary font-bold px-16 py-[14px] rounded-full transition-colors"
                  onClick={onPrevStep}
                >
                  {translations[language]?.back}
                </button>
              </div>

              <div className="hidden sm:flex sm:flex-row items-center space-x-6 w-auto">
                <p className="text-sm font-bold text-gray-600 w-auto text-left">
                  <span className="text-md">
                    {items.length} {translations[language]?.item_length_title}
                  </span>
                  <span className="text-md ml-3">
                    {translations[language]?.total} {checkoutTotal}
                  </span>
                </p>

                {currentStep === 'payment' ? null : (
                  <button
                    onClick={onNextStep}
                    className="flex items-center justify-center text-sm font-bold bg-primary text-white px-8 py-[15px] rounded-full lg:hover:bg-secondary transition-colors w-auto"
                  >
                    <span>
                      {restaurantId === '682d79a98f4810001ac860e1'
                        ? translations[language]?.continue
                        : translations[language]?.pay_and_order_now}
                    </span>
                    <i className="fa fa-chevron-right ml-2" />
                  </button>
                )}
              </div>

              {/* Mobile layout for checkout steps */}
              <div className="flex sm:hidden items-center justify-between w-full">
                <button
                  className="w-[91px] bg-[#fff] hover:bg-primary/20 border !border-primary text-xs text-primary font-bold px-4 py-[11px] rounded-full transition-colors"
                  onClick={onPrevStep}
                >
                  {translations[language]?.back}
                </button>

                <p className="text-xs font-bold text-gray-600 space-x-1 mx-2">
                  <span>
                    {items.length} {translations[language]?.item_length_title}
                  </span>
                  <span>{checkoutTotal}</span>
                </p>

                {currentStep === 'review' && (
                  <button
                    onClick={onNextStep}
                    className="flex items-center justify-center text-xs font-bold bg-primary text-white px-4 py-3 rounded-full lg:hover:bg-secondary transition-colors"
                  >
                    <span>
                      {restaurantId === '682d79a98f4810001ac860e1'
                        ? translations[language]?.continue
                        : translations[language]?.pay_now}
                    </span>
                    <i className="fa fa-chevron-right ml-1" />
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrderSummary;
